import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, KeyRound } from 'lucide-react';
import { aiVisibilityService } from '../../../services/aiVisibilityService';
import { MONITORING_PRICE, MONITORING_PRICE_CENTS } from '../../../config/pricing';
import { CONTACT_EMAIL } from '../../../utils/contact';

// The monthly follow-up, at the end of every finished report. It isn't sold
// yet: visitors can ask to be told when it is (waitlist, stored with the
// price shown) and testers can start a free trial with a follow-up code
// from the admin panel, /admin → Códigos (ai-monitoring).

// Only what the monthly report and email actually deliver.
const WHAT_YOU_GET = [
  'Cada mes volvemos a preguntar a la IA y te decimos si te recomienda más o menos',
  'Quién te ha adelantado y quién lidera en tu zona',
  'Tu siguiente paso, con el plan de acción actualizado',
  'Los textos del «Hecho para ti» renovados con lo último que encuentra la IA'
];

const formatDay = (iso) => new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });

const Waitlist = ({ reportId }) => {
  const [email, setEmail] = useState('');
  const [state, setState] = useState({ status: 'idle', message: null }); // idle | sending | done | error

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!email.trim()) {
      setState({ status: 'error', message: 'Escribe tu email para que podamos avisarte.' });
      return;
    }
    setState({ status: 'sending', message: null });
    const result = await aiVisibilityService.joinMonitoringWaitlist({
      reportId,
      email: email.trim(),
      priceCents: MONITORING_PRICE_CENTS
    });
    setState(result.success ? { status: 'done', message: null } : { status: 'error', message: result.message });
  };

  if (state.status === 'done') {
    return (
      <p role="status" className="mt-8 flex items-center gap-3 font-semibold text-[var(--lp-lime)]">
        <Check size={20} aria-hidden="true" />
        Apuntado. Te escribiremos cuando esté listo.
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="mt-8">
      <label htmlFor="monitoring-email" className="text-xs font-semibold text-[var(--lp-muted)]">
        Tu email
      </label>
      <div className="mt-2 flex flex-col gap-3 sm:flex-row">
        <input
          id="monitoring-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          className="lp-field sm:flex-1"
          placeholder="tu@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          maxLength={254}
        />
        <button type="submit" className="lp-btn" disabled={state.status === 'sending'}>
          {state.status === 'sending' ? 'Apuntando…' : 'Avísame'}
        </button>
      </div>
      {state.status === 'error' && (
        <p role="alert" className="mt-3 text-sm font-medium text-[var(--lp-alarm)]">
          {state.message}
        </p>
      )}
    </form>
  );
};

const TrialCode = ({ reportId, businessName }) => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [state, setState] = useState({ status: 'idle', message: null, result: null });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[var(--lp-muted)] transition-colors hover:text-[var(--lp-text)]"
      >
        <KeyRound size={16} aria-hidden="true" />
        ¿Tienes un código de prueba del seguimiento?
      </button>
    );
  }

  if (state.status === 'done') {
    const { result } = state;
    return (
      <div role="status" className="mt-8 rounded-2xl border border-[var(--lp-lime)] p-5">
        <p className="flex items-center gap-3 font-semibold text-[var(--lp-lime)]">
          <Check size={20} aria-hidden="true" />
          {result.alreadyActive ? 'El seguimiento ya estaba activo.' : 'Seguimiento activado.'}
        </p>
        <p className="mt-2 text-sm leading-relaxed text-[var(--lp-muted)]">
          {result.firstRunAt && <>El primer informe mensual de {businessName} llegará hacia el {formatDay(result.firstRunAt)}. </>}
          {result.activeUntil && <>La prueba es gratuita y dura hasta el {formatDay(result.activeUntil)}. </>}
          {result.emailSent === false
            ? 'El email de bienvenida no ha podido salir; si no recibes nada, escríbenos.'
            : result.alreadyActive
              ? ''
              : `Te hemos enviado un email a ${email.trim()} (mira también en spam).`}
        </p>
      </div>
    );
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!code.trim() || !email.trim()) {
      setState({ status: 'error', message: 'Escribe el código y tu email.', result: null });
      return;
    }
    setState({ status: 'sending', message: null, result: null });
    const result = await aiVisibilityService.activateMonitoring({ reportId, email: email.trim(), code: code.trim() });
    setState(
      result.success
        ? { status: 'done', message: null, result }
        : { status: 'error', message: result.message, result: null }
    );
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="mt-8 rounded-2xl border border-[var(--lp-line)] p-5">
      <p className="flex items-center gap-2 font-semibold">
        <KeyRound size={18} className="text-[var(--lp-lime)]" aria-hidden="true" />
        Activa tu prueba del seguimiento
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="trial-code" className="text-xs font-semibold text-[var(--lp-muted)]">
            Código
          </label>
          <input
            id="trial-code"
            className="lp-field lp-mono mt-2 uppercase"
            placeholder="SEG-XXXX-XXXX"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck="false"
          />
        </div>
        <div>
          <label htmlFor="trial-email" className="text-xs font-semibold text-[var(--lp-muted)]">
            Email donde recibirlo
          </label>
          <input
            id="trial-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            className="lp-field mt-2"
            placeholder="tu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={254}
          />
        </div>
      </div>
      <button type="submit" className="lp-btn mt-5" disabled={state.status === 'sending'}>
        {state.status === 'sending' ? 'Activando…' : 'Activar seguimiento'}
        <ArrowRight size={18} strokeWidth={2.6} aria-hidden="true" />
      </button>
      {state.status === 'error' && (
        <p role="alert" className="mt-3 text-sm font-medium text-[var(--lp-alarm)]">
          {state.message}
        </p>
      )}
    </form>
  );
};

const MonitoringInterest = ({ reportId, businessName }) => (
  <section className="no-print mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
    <div className="grid gap-10 rounded-[28px] border border-[var(--lp-lime)] p-6 sm:p-10 lg:grid-cols-2">
      <div>
        <p className="lp-kicker">Próximamente · Seguimiento mensual</p>
        <h2 className="lp-display mt-4 text-[clamp(2rem,4vw,3rem)]">
          ¿Funciona lo que cambies?{' '}
          <span className="lp-serif text-[var(--lp-lime)]">Te lo contamos cada mes.</span>
        </h2>
        <ul className="mt-8 space-y-3">
          {WHAT_YOU_GET.map((item) => (
            <li key={item} className="flex gap-3 text-[var(--lp-muted)]">
              <ArrowRight size={18} className="mt-1 shrink-0 text-[var(--lp-lime)]" aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-col justify-end">
        <p className="text-lg">
          <span className="lp-display text-4xl">{MONITORING_PRICE}</span>
          <span className="ml-3 text-[var(--lp-muted)]">sin permanencia, te das de baja cuando quieras</span>
        </p>
        <p className="mt-3 text-[var(--lp-muted)]">
          Todavía no está a la venta. Si te interesa para {businessName}, déjanos tu email y te avisamos cuando lo esté.
          No se cobra nada.
        </p>

        <Waitlist reportId={reportId} />
        <TrialCode reportId={reportId} businessName={businessName} />

        <p className="mt-5 text-xs leading-relaxed text-[var(--lp-muted)]">
          Solo usaremos tu email para esto.
          {CONTACT_EMAIL && <> Si quieres que lo borremos, escríbenos a {CONTACT_EMAIL}.</>}{' '}
          <Link to="/legal/privacidad" className="underline underline-offset-4">
            Política de privacidad
          </Link>
        </p>
      </div>
    </div>
  </section>
);

export default MonitoringInterest;
